import React, {useCallback, useMemo, useState} from 'react';
import {usePage} from '@inertiajs/react';
import axios from 'axios';
import {Badge, Button, Card, Col, Container, Form, Row, Table} from 'react-bootstrap';
import {FlexToast, useExternalControls} from 'laravel-inertia-backend';
import HelpTooltip from '../../Components/HelpTooltip';
import SelectList from '../../Components/SelectList';

/**
 * Stammdaten des Auftrags-Editors (PLAN.md 3.6, ABGLEICH O1–O20, N29).
 *
 * Im Original stehen diese Werte in vier JSON-Dateien auf dem Server; eine
 * Änderung brauchte einen Zugriff auf die Maschine und einen Neustart. Hier
 * pflegt sie, wer das Recht „Auftrags-Editor: Stammdaten" hat — und jede
 * Änderung hinterlässt eine Nachweiszeile, weil sie auf jeden künftigen Beleg
 * wirkt.
 *
 * Die Seite gehört nicht zum Original und übernimmt deshalb nicht dessen
 * Optik, sondern die des Ordermanagements (Karten, Tabellen, eigene
 * Auswahllisten statt `<select>`).
 *
 * Gespeichert wird je Karte einzeln: Wer die Rabattstaffeln ändert, soll nicht
 * versehentlich auch die Frachtparameter mitschicken.
 */
export default function Rules() {
    const props = usePage().props;
    const {addToast} = useExternalControls();

    const [groups, setGroups] = useState(() => verknuepfeStaffeln(props.discountGroups || [], props.tiers || []));
    const [staffelChannels, setStaffelChannels] = useState(props.staffelChannels || []);
    const [masterbox, setMasterbox] = useState(props.masterboxRule || leereMasterboxRegel());
    const [articles, setArticles] = useState(props.shippingArticles || []);
    const [terms, setTerms] = useState(props.paymentTerms || []);
    const [freightParams, setFreightParams] = useState(props.freightParams || []);
    const [busy, setBusy] = useState(null);

    const channels = props.channels || [];
    const paymentTypes = props.paymentTypes || [];
    const pallets = props.pallets || null;
    const freightMeta = props.freightMeta || {};
    const unmappedTermNames = props.unmappedTermNames || [];
    const darfPflegen = !!(props.abilities && props.abilities.rules);

    const toast = useCallback((message, variant) => {
        addToast(
            <FlexToast
                title="Auftrags-Editor — Stammdaten"
                variant={variant}
                autohide={variant !== 'danger'}
                message={message}
            />,
        );
    }, [addToast]);

    /** Eine Karte speichern und den zurückgemeldeten Stand bestätigen. */
    const speichern = useCallback(async (karte, url, payload, erfolg) => {
        setBusy(karte);
        try {
            await axios.put(url, payload);
            toast(erfolg);
        } catch (e) {
            const message = e.response?.data?.message || e.response?.data?.error || e.message;
            toast(`Speichern fehlgeschlagen: ${message}`, 'danger');
        } finally {
            setBusy(null);
        }
    }, [toast]);

    // ── Rabattgruppen ───────────────────────────────────────────────────────

    const setGroup = (index, patch) => setGroups(
        (liste) => liste.map((g, i) => (i === index ? {...g, ...patch} : g)),
    );

    const setTier = (gi, ti, patch) => setGroups((liste) => liste.map((g, i) => (i === gi
        ? {...g, tiers: g.tiers.map((t, j) => (j === ti ? {...t, ...patch} : t))}
        : g)));

    const channelOptions = useMemo(
        () => [{value: '', label: '— kein Vertriebsweg —'}]
            .concat(channels.map((c) => ({value: c.code, label: `${c.code} · ${c.label}`}))),
        [channels],
    );

    const speichereGruppen = () => speichern(
        'groups',
        '/order-editor/rules/discount-groups',
        {
            groups: groups.map((g, index) => ({
                key: g.key,
                label: g.label,
                attributeDefinitionId: g.attributeDefinitionId || null,
                channel: g.channel || null,
                channelLabel: g.channelLabel || null,
                channelMinQty: g.channelMinQty === '' || g.channelMinQty == null ? null : Number(g.channelMinQty),
                sort: g.sort != null ? g.sort : index,
                tiers: (g.tiers || [])
                    .filter((t) => String(t.minValue) !== '' && String(t.percent) !== '')
                    .map((t) => ({minValue: Number(String(t.minValue).replace(',', '.')), percent: Number(String(t.percent).replace(',', '.'))})),
            })),
            staffelChannels,
        },
        'Rabattgruppen und Staffeln gespeichert.',
    );

    // ── Masterbox ───────────────────────────────────────────────────────────

    const speichereMasterbox = () => speichern(
        'masterbox',
        '/order-editor/rules/masterbox',
        {
            label: masterbox.label,
            attributeDefinitionId: masterbox.attributeDefinitionId || null,
            fieldDefId: masterbox.fieldDefId || null,
            capsPercent: Number(String(masterbox.capsPercent).replace(',', '.')),
            pumpmarkerPercent: Number(String(masterbox.pumpmarkerPercent).replace(',', '.')),
            transitionalRuleActive: !!masterbox.transitionalRuleActive,
        },
        'Masterbox-Regel gespeichert.',
    );

    // ── Versandartikel ──────────────────────────────────────────────────────

    const setArticle = (index, patch) => setArticles(
        (liste) => liste.map((a, i) => (i === index ? {...a, ...patch} : a)),
    );

    const speichereArtikel = () => speichern(
        'articles',
        '/order-editor/rules/shipping-articles',
        {
            articles: articles.map((a, index) => ({
                articleNumber: a.articleNumber,
                weclappArticleId: String(a.weclappArticleId || ''),
                name: a.name,
                group: a.group || null,
                sort: a.sort != null ? a.sort : index,
                active: !!a.active,
            })),
        },
        'Versandartikel gespeichert.',
    );

    // ── Zahlungsarten ───────────────────────────────────────────────────────

    const setTerm = (index, patch) => setTerms((liste) => liste.map((t, i) => (i === index ? {...t, ...patch} : t)));

    const speichereZahlungsarten = () => speichern(
        'terms',
        '/order-editor/rules/payment-terms',
        {
            terms: terms.map((t) => ({
                termName: t.termName,
                paymentType: t.paymentType,
                weclappTermId: t.weclappTermId || null,
            })),
        },
        'Zahlungsarten-Zuordnung gespeichert.',
    );

    // ── Frachtparameter ─────────────────────────────────────────────────────

    const setParam = (index, value) => setFreightParams(
        (liste) => liste.map((p, i) => (i === index ? {...p, value} : p)),
    );

    const zahlParams = freightParams.filter((p) => p.valueJson === null || p.valueJson === undefined);

    const speichereFracht = () => speichern(
        'freight',
        '/order-editor/rules/freight-params',
        {
            params: zahlParams.map((p) => ({
                id: p.id,
                value: Number(String(p.value ?? 0).replace(',', '.')),
            })),
        },
        'Frachtparameter gespeichert.',
    );

    const speicherKnopf = (karte, onClick, hilfe) => (
        <div className="d-flex justify-content-end align-items-center gap-2 mt-3">
            <HelpTooltip id={`oe-rules-save-${karte}`} placement="left" size={0.8}>{hilfe}</HelpTooltip>
            <Button variant="primary" disabled={!darfPflegen || busy === karte} onClick={onClick}>
                {busy === karte ? 'Speichern…' : 'Speichern'}
            </Button>
        </div>
    );

    return (
        <Container fluid className="mt-3">
            <h1 className="d-inline-flex align-items-center gap-2 h3">
                Auftrags-Editor — Stammdaten
                <HelpTooltip
                    id="oe-rules-help"
                    placement="right"
                    size={0.95}
                    detail={
                        <div className="text-start">
                            Geregelt sind Rabattstaffeln, Masterbox-Rabatt, freigegebene Versandartikel, Zuordnung
                            der Zahlungsbedingungen und die Zuschläge der Frachtrechnung.
                        </div>
                    }
                >
                    <div className="text-start">
                        <strong>Wozu dient diese Seite?</strong>
                        <div className="mt-1">
                            Hier stehen die Regeln, nach denen der Auftrags-Editor rechnet. Jede Änderung wirkt
                            sofort auf alle künftig geöffneten Belege und wird protokolliert.
                        </div>
                    </div>
                </HelpTooltip>
            </h1>

            {!darfPflegen ? (
                <div className="alert alert-warning">
                    Nur Leserecht — zum Ändern fehlt das Recht „Auftrags-Editor: Stammdaten".
                </div>
            ) : null}

            {/* ── Rabattgruppen ─────────────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Header as="h5">
                    Rabattgruppen und Staffeln
                    <HelpTooltip
                        id="oe-rules-groups"
                        placement="right"
                        size={0.8}
                        detail={<>
                            Der Warenwert zählt nur die <b>lieferbare</b> Menge. Die Zuordnung Artikel → Gruppe
                            kommt aus den Artikel-Checkboxen in weclapp (Attribut-Kennung).
                        </>}
                    >
                        Je Rabattgruppe die Prozentstaffel („ab X € Warenwert gilt Y %"). Es gilt immer die
                        höchste erreichte Stufe.
                    </HelpTooltip>
                </Card.Header>
                <Card.Body>
                    <Row className="mb-3">
                        <Col md={8}>
                            <Form.Label className="d-flex align-items-center gap-2">
                                Vertriebswege mit Staffelrabatt
                                <HelpTooltip id="oe-rules-staffelchannels" placement="right" size={0.8}>
                                    Nur in diesen Preisgruppen greifen die Prozentstaffeln. In den übrigen
                                    (zum Beispiel PG5) ist der Preis bereits der beste — dort zeigt der Editor
                                    „Staffel gesperrt".
                                </HelpTooltip>
                            </Form.Label>
                            <div className="d-flex flex-wrap gap-3">
                                {channels.map((c) => (
                                    <Form.Check
                                        key={c.code}
                                        type="checkbox"
                                        id={`oe-staffelkanal-${c.code}`}
                                        label={`${c.code} · ${c.label}`}
                                        disabled={!darfPflegen}
                                        checked={staffelChannels.includes(c.code)}
                                        onChange={(e) => setStaffelChannels(
                                            e.target.checked
                                                ? staffelChannels.concat([c.code])
                                                : staffelChannels.filter((x) => x !== c.code),
                                        )}
                                    />
                                ))}
                            </div>
                        </Col>
                    </Row>

                    {groups.map((g, gi) => (
                        <Card className="mb-3" key={g.key}>
                            <Card.Body>
                                <Row className="g-2 align-items-end">
                                    <Col md={2}>
                                        <Form.Label className="small text-muted mb-1">Schlüssel</Form.Label>
                                        <Form.Control value={g.key} readOnly plaintext/>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Bezeichnung
                                            <HelpTooltip id={`oe-rules-glabel-${g.key}`} size={0.7}>
                                                Name, der im Editor auf der Rabattkarte und im Ticker steht.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            value={g.label || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setGroup(gi, {label: e.target.value})}
                                        />
                                    </Col>
                                    <Col md={3}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Vertriebsweg (Palettenpreis)
                                            <HelpTooltip id={`oe-rules-gchannel-${g.key}`} size={0.7}>
                                                Statt eines Prozentsatzes schaltet diese Gruppe ab der Mindestmenge
                                                auf einen anderen Vertriebsweg um — den Palettenpreis. Leer lassen,
                                                wenn die Gruppe mit Prozentstaffeln arbeitet.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <SelectList
                                            value={g.channel || ''}
                                            options={channelOptions}
                                            disabled={!darfPflegen}
                                            ariaLabel={`Vertriebsweg der Gruppe ${g.label}`}
                                            onChange={(v) => setGroup(gi, {channel: v || null})}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Mindestmenge
                                            <HelpTooltip id={`oe-rules-gminqty-${g.key}`} size={0.7}>
                                                Ab dieser Stückzahl (über alle Positionen der Gruppe) gilt der
                                                Palettenpreis.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            inputMode="numeric"
                                            value={g.channelMinQty ?? ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setGroup(gi, {channelMinQty: e.target.value.replace(/[^\d]/g, '')})}
                                        />
                                    </Col>
                                    <Col md={2}>
                                        <Form.Label className="small text-muted mb-1 d-flex align-items-center gap-1">
                                            Attribut-Kennung
                                            <HelpTooltip id={`oe-rules-gdef-${g.key}`} size={0.7}>
                                                Kennung der Artikel-Checkbox in weclapp, über die Artikel dieser
                                                Gruppe zugeordnet werden.
                                            </HelpTooltip>
                                        </Form.Label>
                                        <Form.Control
                                            inputMode="numeric"
                                            value={g.attributeDefinitionId || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setGroup(gi, {attributeDefinitionId: e.target.value.replace(/[^\d]/g, '')})}
                                        />
                                    </Col>
                                </Row>

                                <div className="mt-3">
                                    <div className="small text-muted mb-1 d-flex align-items-center gap-1">
                                        Staffeln
                                        <HelpTooltip id={`oe-rules-tiers-${g.key}`} size={0.7}>
                                            „ab Warenwert" in Euro, „Rabatt" in Prozent. Leere Zeilen werden beim
                                            Speichern verworfen; entfernte Stufen verschwinden dauerhaft.
                                        </HelpTooltip>
                                    </div>
                                    <Table size="sm" className="mb-2" style={{maxWidth: 520}}>
                                        <thead>
                                            <tr>
                                                <th>ab Warenwert (€)</th>
                                                <th>Rabatt (%)</th>
                                                <th/>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(g.tiers || []).map((t, ti) => (
                                                <tr key={ti}>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            inputMode="decimal"
                                                            aria-label="ab Warenwert in Euro"
                                                            value={t.minValue}
                                                            disabled={!darfPflegen}
                                                            onChange={(e) => setTier(gi, ti, {minValue: e.target.value})}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            size="sm"
                                                            inputMode="decimal"
                                                            aria-label="Rabatt in Prozent"
                                                            value={t.percent}
                                                            disabled={!darfPflegen}
                                                            onChange={(e) => setTier(gi, ti, {percent: e.target.value})}
                                                        />
                                                    </td>
                                                    <td className="text-end">
                                                        <HelpTooltip
                                                            id={`oe-rules-tierdel-${g.key}-${ti}`}
                                                            placement="left"
                                                            size={0.8}
                                                        >
                                                            Entfernt diese Stufe aus der Staffel. Wirksam wird das
                                                            mit „Speichern".
                                                        </HelpTooltip>
                                                        <Button
                                                            size="sm"
                                                            variant="outline-secondary"
                                                            disabled={!darfPflegen}
                                                            onClick={() => setGroup(gi, {tiers: g.tiers.filter((_, j) => j !== ti)})}
                                                        >
                                                            ✕
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                    <Button
                                        size="sm"
                                        variant="outline-primary"
                                        disabled={!darfPflegen}
                                        onClick={() => setGroup(gi, {tiers: (g.tiers || []).concat([{minValue: '', percent: ''}])})}
                                    >
                                        + Stufe
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    ))}

                    {speicherKnopf('groups', speichereGruppen,
                        'Schreibt alle Gruppen samt Staffeln und die Liste der Vertriebswege mit Staffelrabatt.')}
                </Card.Body>
            </Card>

            {/* ── Masterbox ─────────────────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Header as="h5">
                    Masterbox-Regel
                    <HelpTooltip id="oe-rules-mb" placement="right" size={0.8}>
                        Artikel mit gepflegter Masterboxgröße bekommen den Rabatt nur auf die Stück in vollen
                        Boxen; der Rest bleibt ohne. Der Rabatt gilt für jeden Kunden, unabhängig vom
                        Vertriebsweg.
                    </HelpTooltip>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col md={3}>
                            <Form.Label className="d-flex align-items-center gap-1">
                                Bezeichnung
                                <HelpTooltip id="oe-rules-mblabel" size={0.7}>
                                    Name der Regel auf der Rabattkarte im Editor.
                                </HelpTooltip>
                            </Form.Label>
                            <Form.Control
                                value={masterbox.label || ''}
                                disabled={!darfPflegen}
                                onChange={(e) => setMasterbox({...masterbox, label: e.target.value})}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="d-flex align-items-center gap-1">
                                Kennung Boxgröße
                                <HelpTooltip id="oe-rules-mbattr" size={0.7}>
                                    Kennung des weclapp-Artikelfelds mit der Masterboxgröße (Stück je Box).
                                </HelpTooltip>
                            </Form.Label>
                            <Form.Control
                                inputMode="numeric"
                                value={masterbox.attributeDefinitionId || ''}
                                disabled={!darfPflegen}
                                onChange={(e) => setMasterbox({...masterbox, attributeDefinitionId: e.target.value.replace(/[^\d]/g, '')})}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="d-flex align-items-center gap-1">
                                Kennung Prozentsatz
                                <HelpTooltip id="oe-rules-mbfield" size={0.7}>
                                    Kennung des weclapp-Artikelfelds „Masterbox Rabatt". Ist es gefüllt, gilt
                                    dieser Satz — vor jeder Übergangsregel.
                                </HelpTooltip>
                            </Form.Label>
                            <Form.Control
                                inputMode="numeric"
                                value={masterbox.fieldDefId || ''}
                                disabled={!darfPflegen}
                                onChange={(e) => setMasterbox({...masterbox, fieldDefId: e.target.value.replace(/[^\d]/g, '')})}
                            />
                        </Col>
                    </Row>

                    <hr/>

                    <Row className="g-3 align-items-end">
                        <Col md={12}>
                            <Form.Check
                                type="switch"
                                id="oe-rules-mbtransitional"
                                disabled={!darfPflegen}
                                checked={!!masterbox.transitionalRuleActive}
                                onChange={(e) => setMasterbox({...masterbox, transitionalRuleActive: e.target.checked})}
                                label={
                                    <span className="d-inline-flex align-items-center gap-2">
                                        Übergangsregel aktiv
                                        <HelpTooltip
                                            id="oe-rules-mbtrans"
                                            size={0.8}
                                            detail={
                                                <div className="text-start">
                                                    Solange sie an ist, gilt bei leerem Feld: Artikelnummer beginnt
                                                    mit „9000" (Caps) → Caps-Prozentsatz, sonst Rabattgruppe
                                                    Pumpmarker → Pumpmarker-Prozentsatz, sonst kein Rabatt.
                                                    Ausschalten, sobald das Feld überall gepflegt ist — vorher den
                                                    Pflegestand prüfen.
                                                </div>
                                            }
                                        >
                                            <div className="text-start">
                                                Die Übergangsregel ist <b>befristet</b>, bis das weclapp-Artikelfeld
                                                „Masterbox Rabatt" flächig gepflegt ist.
                                            </div>
                                        </HelpTooltip>
                                    </span>
                                }
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="d-flex align-items-center gap-1">
                                Caps-Prozentsatz (%)
                                <HelpTooltip id="oe-rules-mbcaps" size={0.7}>
                                    Gilt bei leerem Artikelfeld für Artikelnummern, die mit „9000" beginnen.
                                </HelpTooltip>
                            </Form.Label>
                            <Form.Control
                                inputMode="decimal"
                                value={masterbox.capsPercent ?? ''}
                                disabled={!darfPflegen || !masterbox.transitionalRuleActive}
                                onChange={(e) => setMasterbox({...masterbox, capsPercent: e.target.value})}
                            />
                        </Col>
                        <Col md={3}>
                            <Form.Label className="d-flex align-items-center gap-1">
                                Pumpmarker-Prozentsatz (%)
                                <HelpTooltip id="oe-rules-mbpump" size={0.7}>
                                    Gilt bei leerem Artikelfeld für Artikel der Rabattgruppe Pumpmarker.
                                </HelpTooltip>
                            </Form.Label>
                            <Form.Control
                                inputMode="decimal"
                                value={masterbox.pumpmarkerPercent ?? ''}
                                disabled={!darfPflegen || !masterbox.transitionalRuleActive}
                                onChange={(e) => setMasterbox({...masterbox, pumpmarkerPercent: e.target.value})}
                            />
                        </Col>
                    </Row>

                    {speicherKnopf('masterbox', speichereMasterbox,
                        'Schreibt die Masterbox-Regel samt Schalter der Übergangsregel.')}
                </Card.Body>
            </Card>

            {/* ── Versandartikel ────────────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Header as="h5">
                    Versandartikel
                    <HelpTooltip id="oe-rules-ship" placement="right" size={0.8}>
                        Nur diese Artikel lassen sich im Editor als Versandkosten auf einen Auftrag schreiben.
                        Was hier fehlt oder inaktiv ist, weist der Server beim Speichern zurück.
                    </HelpTooltip>
                </Card.Header>
                <Card.Body>
                    <Table responsive size="sm">
                        <thead>
                            <tr>
                                <th>Nummer</th>
                                <th>weclapp-Kennung</th>
                                <th>Name</th>
                                <th>Gruppe</th>
                                <th>aktiv</th>
                            </tr>
                        </thead>
                        <tbody>
                            {articles.map((a, i) => (
                                <tr key={a.id || a.articleNumber}>
                                    <td style={{minWidth: 120}}>
                                        <Form.Control
                                            size="sm"
                                            aria-label="Artikelnummer"
                                            value={a.articleNumber || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setArticle(i, {articleNumber: e.target.value})}
                                        />
                                    </td>
                                    <td style={{minWidth: 130}}>
                                        <Form.Control
                                            size="sm"
                                            inputMode="numeric"
                                            aria-label="weclapp-Artikelkennung"
                                            value={a.weclappArticleId || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setArticle(i, {weclappArticleId: e.target.value.replace(/[^\d]/g, '')})}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            size="sm"
                                            aria-label="Artikelname"
                                            value={a.name || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setArticle(i, {name: e.target.value})}
                                        />
                                    </td>
                                    <td style={{minWidth: 150}}>
                                        <SelectList
                                            size="sm"
                                            value={a.group || ''}
                                            options={GRUPPEN_OPTIONEN}
                                            disabled={!darfPflegen}
                                            ariaLabel={`Gruppe von ${a.articleNumber}`}
                                            onChange={(v) => setArticle(i, {group: v})}
                                        />
                                    </td>
                                    <td>
                                        <Form.Check
                                            type="switch"
                                            id={`oe-rules-shipactive-${a.articleNumber}`}
                                            aria-label={`Artikel ${a.articleNumber} aktiv`}
                                            checked={!!a.active}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setArticle(i, {active: e.target.checked})}
                                        />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {pallets ? (
                        <div className="text-muted small d-flex align-items-center gap-2">
                            Palettenparameter der Schätzung: Nutzhöhe {pallets.heightM} m, Füllfaktor
                            {' '}{pallets.fillFactor}, Typen {(pallets.types || []).map((t) => `${t.label} (${t.l} × ${t.w} m)`).join(' · ')}
                            <HelpTooltip
                                id="oe-rules-pallets"
                                size={0.7}
                                detail={<>
                                    Nutzhöhe und Füllfaktor stehen in den Systemeinstellungen (Kategorie
                                    „Auftrags-Editor"), die Maße in der Konfiguration.
                                </>}
                            >
                                Grundlage der Paletten-Schätzung im Versandschritt — ausdrücklich nur ein Vorschlag,
                                der Mitarbeiter entscheidet.
                            </HelpTooltip>
                        </div>
                    ) : null}

                    {speicherKnopf('articles', speichereArtikel,
                        'Schreibt die Liste der freigegebenen Versandartikel.')}
                </Card.Body>
            </Card>

            {/* ── Zahlungsarten ─────────────────────────────────────────── */}
            <Card className="mb-4">
                <Card.Header as="h5">
                    Zahlungsarten-Zuordnung
                    <HelpTooltip id="oe-rules-terms" placement="right" size={0.8}>
                        Ordnet den Namen einer weclapp-Zahlungsbedingung der Auftragsart zu, die der Editor
                        vorschlägt (Standard, Vorkasse, Anzahlung).
                    </HelpTooltip>
                </Card.Header>
                <Card.Body>
                    <div className="alert alert-warning py-2 small">
                        <b>Achtung, fragile Schlüssel:</b> Die Zuordnung hängt am <b>Namen</b> der
                        Zahlungsbedingung, nicht an ihrer Kennung. Wird sie in weclapp umbenannt oder mit
                        Tippfehler angelegt, fällt der Editor still auf die Ersatzregel zurück (enthält „50 %"
                        und „Voraus" → Anzahlung; enthält „Voraus"/„Vorkasse" → Vorkasse; sonst Standard).
                        Deshalb möglichst die weclapp-Kennung mit eintragen.
                    </div>

                    <Table responsive size="sm">
                        <thead>
                            <tr>
                                <th>Zahlungsbedingung (Name in weclapp)</th>
                                <th style={{width: 220}}>Auftragsart</th>
                                <th style={{width: 180}}>weclapp-Kennung</th>
                                <th style={{width: 110}}>Quelle</th>
                            </tr>
                        </thead>
                        <tbody>
                            {terms.map((t, i) => (
                                <tr key={t.id || t.termName}>
                                    <td>{t.termName}</td>
                                    <td>
                                        <SelectList
                                            size="sm"
                                            value={t.paymentType || ''}
                                            options={paymentTypes.map((p) => ({value: p.id, label: p.label}))}
                                            disabled={!darfPflegen}
                                            ariaLabel={`Auftragsart für ${t.termName}`}
                                            onChange={(v) => setTerm(i, {paymentType: v})}
                                        />
                                    </td>
                                    <td>
                                        <Form.Control
                                            size="sm"
                                            inputMode="numeric"
                                            aria-label={`weclapp-Kennung für ${t.termName}`}
                                            value={t.weclappTermId || ''}
                                            disabled={!darfPflegen}
                                            onChange={(e) => setTerm(i, {weclappTermId: e.target.value.replace(/[^\d]/g, '')})}
                                        />
                                    </td>
                                    <td>
                                        <Badge bg={t.source === 'manual' ? 'primary' : 'secondary'}>
                                            {t.source === 'manual' ? 'von Hand' : (t.source || 'Import')}
                                        </Badge>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {/* ABGLEICH O20: Genau hier wird die Fragilität der
                        Freitext-Schlüssel sichtbar — Bedingungen, die es in
                        weclapp gibt, die aber in keiner Zeile oben stehen. */}
                    {unmappedTermNames.length ? (
                        <div className="alert alert-danger py-2 small mb-3">
                            <b>{unmappedTermNames.length} Zahlungsbedingung(en) in weclapp ohne Zuordnung</b> —
                            für sie greift nur die Ersatzregel. Bitte oben als Zeile ergänzen:
                            <ul className="mb-0 mt-1">
                                {unmappedTermNames.map((t) => (
                                    <li key={t.id || t.name}>
                                        {t.name} <span className="text-muted">(weclapp-Kennung {t.id || '—'})</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : (
                        <div className="alert alert-success py-2 small mb-3">
                            Alle Zahlungsbedingungen aus weclapp sind zugeordnet.
                        </div>
                    )}

                    {speicherKnopf('terms', speichereZahlungsarten,
                        'Schreibt die Zuordnung. Von Hand gepflegte Zeilen überschreibt ein späterer Import nicht.')}
                </Card.Body>
            </Card>

            {/* ── Frachtparameter ───────────────────────────────────────── */}
            <Card className="mb-5">
                <Card.Header as="h5">
                    Frachtparameter
                    <HelpTooltip id="oe-rules-freight" placement="right" size={0.8}>
                        Die Zuschläge und Rechengrößen der Frachtrechnung. Die Tarif-Tabellen selbst kommen aus
                        dem jährlichen Import des Molotow-Kostenrechners und lassen sich hier nicht ändern.
                    </HelpTooltip>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3 mb-3 small">
                        <Col md={3}>
                            <div className="text-muted">Stand der Tarife</div>
                            <b>{freightMeta.stand || 'unbekannt'}</b>
                        </Col>
                        <Col md={3}>
                            <div className="text-muted">Letzter Import</div>
                            <b>{freightMeta.lastImport || '—'}</b>
                            {freightMeta.importedFrom ? (
                                <div className="text-muted">aus {freightMeta.importedFrom}</div>
                            ) : null}
                        </Col>
                        <Col md={3}>
                            <div className="text-muted">Länder</div>
                            <b>{(freightMeta.countries || []).length}</b>
                            <div className="text-muted">
                                {(freightMeta.countries || []).map((c) => c.iso).join(', ')}
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="text-muted d-flex align-items-center gap-1">
                                Tarifzellen
                                <HelpTooltip id="oe-rules-freightcells" size={0.7}>
                                    Anzahl der importierten Preiszellen (Frachtführer × Land × PLZ-Zone ×
                                    Gewichtsstufe). Der Editor lädt davon je Beleg nur die Teilmenge des
                                    Ziellands.
                                </HelpTooltip>
                            </div>
                            <b>{freightMeta.rateCount ?? 0}</b>
                            <div className="text-muted">
                                Frachtführer: {(freightMeta.carriers || []).join(', ') || '—'}
                            </div>
                        </Col>
                    </Row>

                    {freightMeta.bekannteAbweichung ? (
                        <div className="alert alert-info py-2 small">
                            <b>Bekannte Abweichung:</b> {freightMeta.bekannteAbweichung}
                        </div>
                    ) : null}

                    <Table responsive size="sm">
                        <thead>
                            <tr>
                                <th style={{width: 180}}>Frachtführer</th>
                                <th>Parameter</th>
                                <th style={{width: 100}}>Land</th>
                                <th style={{width: 160}}>Wert</th>
                            </tr>
                        </thead>
                        <tbody>
                            {freightParams.map((p, i) => (
                                <tr key={p.id}>
                                    <td>{p.carrier || <span className="text-muted">alle</span>}</td>
                                    <td>
                                        {p.paramKey}
                                        {p.note ? <div className="text-muted small">{p.note}</div> : null}
                                    </td>
                                    <td>{p.countryIso || <span className="text-muted">alle</span>}</td>
                                    <td>
                                        {(p.valueJson === null || p.valueJson === undefined) ? (
                                            <Form.Control
                                                size="sm"
                                                inputMode="decimal"
                                                aria-label={`Wert von ${p.paramKey}`}
                                                value={p.value ?? ''}
                                                disabled={!darfPflegen}
                                                onChange={(e) => setParam(i, e.target.value)}
                                            />
                                        ) : (
                                            <span className="text-muted small">
                                                {JSON.stringify(p.valueJson)}
                                            </span>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>

                    {speicherKnopf('freight', speichereFracht,
                        'Schreibt die Zahlenwerte der Frachtparameter. Listen-Werte bleiben unverändert — sie '
                        + 'entstehen beim Import.')}
                </Card.Body>
            </Card>
        </Container>
    );
}

/** Auswahl der Versandartikel-Gruppen (Original `shipping-articles.json`). */
const GRUPPEN_OPTIONEN = [
    {value: 'Palette', label: 'Palette'},
    {value: 'Fracht', label: 'Fracht'},
    {value: 'Paket', label: 'Paket'},
];

/** Staffeln aus der flachen Liste an ihre Gruppe hängen. */
function verknuepfeStaffeln(groups, tiers) {
    return groups.map((g) => ({
        ...g,
        channelMinQty: g.channelMinQty ?? '',
        tiers: tiers
            .filter((t) => t.groupId === g.id)
            .sort((a, b) => a.minValue - b.minValue)
            .map((t) => ({minValue: t.minValue, percent: t.percent})),
    }));
}

function leereMasterboxRegel() {
    return {
        label: 'Masterbox-Rabatt',
        attributeDefinitionId: '',
        fieldDefId: '',
        capsPercent: 5,
        pumpmarkerPercent: 7.5,
        transitionalRuleActive: true,
    };
}
